import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from './Button';
import Icon from '../AppIcon';

const TBF_LOGO_SRC = '/assets/images/tbf-logo-header.png';

const Header = ({ title, subtitle, showBackButton = false, onBack, children }) => {
  const navigate = useNavigate();
  const { user, userProfile, signOut, loading } = useAuth();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    setIsAccountMenuOpen(false);
    try {
      const { error } = await signOut();
      if (!error) {
        localStorage.removeItem('tbf_user');
        localStorage.removeItem('tbf_remember_me');
        navigate('/login', { replace: true });
      }
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setIsSigningOut(false);
    }
  };

  useEffect(() => {
    if (!isAccountMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setIsAccountMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isAccountMenuOpen]);

  const displayName = userProfile?.full_name || user?.email?.split('@')?.[0] || 'User';
  const displayRole = userProfile?.role || 'User';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-white border-b border-gray-200 nav-shadow">
      <div className="flex h-full items-center justify-between px-4">
        <div className="flex items-center gap-4 min-w-0">
          <Link
            to="/"
            className="flex items-center shrink-0"
            title="Tanzania Basketball Federation"
          >
            <img
              src={TBF_LOGO_SRC}
              alt="Tanzania Basketball Federation"
              className="h-10 w-auto object-contain"
            />
          </Link>

          {showBackButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              iconName="ArrowLeft"
              iconPosition="left"
            >
              Back
            </Button>
          )}

          {(title || subtitle) && (
            <div className="hidden sm:block min-w-0 border-l border-gray-200 pl-4">
              {title && (
                <h1 className="text-base font-semibold text-gray-900 truncate">{title}</h1>
              )}
              {subtitle && (
                <p className="text-xs text-gray-600 truncate">{subtitle}</p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center space-x-3 shrink-0">
          {children}

          {user && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                iconName="Home"
                iconPosition="left"
                title="Portal Home"
              >
                Home
              </Button>

              <div className="relative" ref={accountMenuRef}>
                <button
                  type="button"
                  onClick={() => setIsAccountMenuOpen((open) => !open)}
                  className="flex items-center space-x-2 rounded-md px-2 py-1.5 hover:bg-muted/60 transition-colors"
                  aria-haspopup="menu"
                  aria-expanded={isAccountMenuOpen}
                  title="Account menu"
                >
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center overflow-hidden">
                    {userProfile?.avatar_url ? (
                      <img
                        src={userProfile.avatar_url}
                        alt={displayName}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <Icon name="User" size={16} className="text-primary" />
                    )}
                  </div>

                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-gray-900">{displayName}</p>
                    <p className="text-xs text-gray-500 capitalize">{displayRole}</p>
                  </div>

                  <Icon
                    name="ChevronDown"
                    size={16}
                    className={`text-gray-500 transition-transform ${
                      isAccountMenuOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isAccountMenuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 mt-2 w-56 rounded-lg border border-border bg-card shadow-lg z-50 overflow-hidden"
                  >
                    <div className="px-3 py-2 border-b border-border">
                      <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                      <p className="text-xs text-muted-foreground capitalize truncate">
                        {displayRole}
                      </p>
                    </div>

                    <button
                      type="button"
                      role="menuitem"
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-foreground hover:bg-muted/60 transition-colors"
                      onClick={() => {
                        setIsAccountMenuOpen(false);
                        navigate('/profile');
                      }}
                    >
                      <Icon name="UserCog" size={16} className="text-muted-foreground" />
                      Profile Management
                    </button>

                    <button
                      type="button"
                      role="menuitem"
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-foreground hover:bg-muted/60 transition-colors border-t border-border disabled:opacity-60"
                      onClick={handleSignOut}
                      disabled={isSigningOut || loading}
                    >
                      <Icon name="LogOut" size={16} className="text-muted-foreground" />
                      {isSigningOut ? 'Signing Out...' : 'Sign Out'}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {!user && !loading && (
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/login')}
              iconName="LogIn"
              iconPosition="left"
            >
              Sign In
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
